'use strict'

const lodash = require('lodash')
const Big = require('big.js').default
const createMasterOracle = require('master-oracle-lib')
const createMetronomeSynthInfo = require('metronome-synth-info-lib')
const { getPoolVersion } = require('metronome-synth-info-lib/src/utils')
const parseReceiptEvents = require('web3-parse-receipt-events')
const {
  createIsNativeToken,
  fromUnit,
  handleForkedChainId,
  toUnit,
  getMETPrice
} = require('metronome-utils')
const { findTokenByAddress } = require('token-list')
const createExecutor = require('transactions-executor')
const { findByChainId } = require('chain-list')
const debug = require('debug')('vesper-synth-user-lib')
const erc20Abi = require('erc-20-abi')
const {
  getMerkleProofFile,
  decodeSgSwapEvents,
  amountMinFromSlippage
} = require('./utils')
const LayerZeroCustomEventsAbi = require('./abi/LayerZeroCustomEvents.json')
const web3EthAbi = require('web3-eth-abi')
const stargateComposerAbi = require('./abi/StargateComposer.json')
const stargateFactoryAbi = require('./abi/StargateFactory.json')
const stargatePoolAbi = require('./abi/StargatePool.json')

const DEFAULT_VERSION = '1.3.0'

const createMetronomeSynthUser = function (web3, options = {}) {
  const { from, poolAddress, additionalCollaterals = [] } = options

  if (!poolAddress) {
    throw new Error('Pool address is required')
  }

  const encodeTxsAbi = txs =>
    txs.map(({ gas, value = '0', method }) => ({
      createdAt: Date.now(),
      gas,
      tx: {
        data: method.encodeABI(),
        from,
        to: method._parent._address,
        value
      }
    }))

  return web3.eth
    .getChainId()
    .then(handleForkedChainId)
    .then(function (chainId) {
      const {
        decimals: nativeTokenDecimals,
        nativeTokenSymbol,
        wrappedToken
      } = findByChainId(chainId)

      return createMetronomeSynthInfo(web3, { poolAddress }).then(
        ({ getInfo, getNativeTokenGateway, getRewardsDistributors }) =>
          (options.version
            ? Promise.resolve(options.version)
            : getPoolVersion(web3, poolAddress)
          ).then(function (version) {
            let abis
            try {
              abis = require(`./abi/${version}`)
            } catch (err) {
              abis = require(`./abi/${DEFAULT_VERSION}`)
            }
            const {
              crossChainDispatcherAbi,
              debtTokenAbi,
              depositTokenAbi,
              feeProviderAbi,
              nativeTokenGatewayAbi,
              poolAbi,
              poolRegistryAbi,
              quoterAbi,
              smartFarmingManagerAbi,
              syntheticTokenAbi
            } = abis

            const pool = new web3.eth.Contract(poolAbi, poolAddress)

            const getMasterOracle = () =>
              pool.methods
                .masterOracle()
                .call()
                .then(masterOracleAddress =>
                  createMasterOracle(web3, { address: masterOracleAddress })
                )

            return Promise.all([
              options.initialData
                ? Promise.resolve(options.initialData)
                : getInfo(),
              getMasterOracle(),
              getNativeTokenGateway(),
              getRewardsDistributors()
            ]).then(function ([
              metronomeSynthInfo,
              masterOracle,
              nativeTokenGatewayAddress,
              rewardsDistributors
            ]) {
              const nativeTokenGateway = new web3.eth.Contract(
                nativeTokenGatewayAbi,
                nativeTokenGatewayAddress
              )

              const {
                collaterals,
                synthetics,
                fees: { feeProviderAddress, tiers }
              } = metronomeSynthInfo

              const feeProvider = new web3.eth.Contract(
                feeProviderAbi,
                feeProviderAddress
              )

              const isNativeToken = createIsNativeToken(chainId)

              const convertFromUsd = function (asset, amount) {
                if (parseInt(amount)) {
                  return masterOracle.quoteUsdToToken(asset, amount)
                }
                return Promise.resolve('0')
              }

              const convertToUsd = function (asset, amount) {
                if (parseInt(amount)) {
                  return masterOracle.quoteTokenToUsd(asset, amount)
                }
                return Promise.resolve('0')
              }

              const convert = function (assetIn, assetOut, amount) {
                const checkNativeToken = asset =>
                  asset === nativeTokenSymbol ? wrappedToken.address : asset
                return parseInt(amount)
                  ? masterOracle.quote(
                      checkNativeToken(assetIn),
                      checkNativeToken(assetOut),
                      amount
                    )
                  : Promise.resolve('0')
              }

              const formatUsd = (usd = 0) =>
                // USD prices have 18 decimals instead of 6
                Big(usd).div('1e18').toNumber()

              const getDepositByCollateral = collateral =>
                collaterals.find(({ address }) => address === collateral)
                  .depositToken

              const uniqCollaterals = lodash.uniqBy(
                [...collaterals, ...additionalCollaterals],
                'address'
              )

              // additionalCollaterals are only taken into account to requests
              // their balances. This is currently only needed for smart farming,
              // in the case a token there is not in the list of collaterals for
              // the synth section. This will not be needed once smart farming
              // gets it's own app.
              const getCollateralBalances = () =>
                Promise.all(
                  uniqCollaterals.map(
                    ({ address: collateralTokenAddress, symbol, decimals }) =>
                      Promise.resolve()
                        .then(function () {
                          if (isNativeToken(symbol)) {
                            return web3.eth.getBalance(from)
                          }

                          const collateralContract = new web3.eth.Contract(
                            erc20Abi,
                            collateralTokenAddress
                          )

                          return collateralContract.methods
                            .balanceOf(from)
                            .call()
                        })
                        .then(balance => [
                          collateralTokenAddress,
                          {
                            balance,
                            decimals
                          }
                        ])
                  )
                ).then(balance => lodash.fromPairs(balance))

              const getSyntheticTokenBalances = () =>
                Promise.all(
                  synthetics.map(function ({ address: syntheticTokenAddress }) {
                    const syntheticContract = new web3.eth.Contract(
                      syntheticTokenAbi,
                      syntheticTokenAddress
                    )

                    return Promise.all([
                      syntheticContract.methods.balanceOf(from).call(),
                      syntheticContract.methods.decimals().call()
                    ]).then(([balance, decimals]) => [
                      syntheticTokenAddress,
                      {
                        balance,
                        decimals
                      }
                    ])
                  })
                ).then(balance => lodash.fromPairs(balance))

              const getDebtTokens = () =>
                pool.methods.getDebtTokensOfAccount(from).call()

              const getDebtTokenBalances = () =>
                getDebtTokens().then(debtTokenAddresses =>
                  Promise.all(
                    debtTokenAddresses.map(function (debtTokenAddress) {
                      const debtContract = new web3.eth.Contract(
                        debtTokenAbi,
                        debtTokenAddress
                      )

                      return Promise.all([
                        debtContract.methods.balanceOf(from).call(),
                        debtContract.methods.decimals().call(),
                        debtContract.methods.symbol().call(),
                        debtContract.methods.syntheticToken().call()
                      ]).then(([balance, decimals, symbol, syntheticToken]) => [
                        syntheticToken,
                        {
                          address: debtTokenAddress,
                          balance,
                          decimals,
                          symbol
                        }
                      ])
                    })
                  ).then(balance => lodash.fromPairs(balance))
                )

              const getDepositTokenBalances = () =>
                Promise.all(
                  collaterals.map(function ({
                    depositToken: depositTokenAddress
                  }) {
                    const depositContract = new web3.eth.Contract(
                      depositTokenAbi,
                      depositTokenAddress
                    )

                    return Promise.all([
                      depositContract.methods.balanceOf(from).call(),
                      depositContract.methods.decimals().call(),
                      depositContract.methods.lockedBalanceOf(from).call(),
                      depositContract.methods.symbol().call(),
                      depositContract.methods.unlockedBalanceOf(from).call(),
                      depositContract.methods.underlying().call()
                    ]).then(
                      ([
                        balance,
                        decimals,
                        lockedBalance,
                        symbol,
                        unlockedBalance,
                        underlying
                      ]) => [
                        underlying === wrappedToken.address
                          ? nativeTokenSymbol
                          : underlying,
                        {
                          address: depositTokenAddress,
                          balance,
                          decimals,
                          lockedBalance,
                          symbol,
                          unlockedBalance
                        }
                      ]
                    )
                  })
                ).then(balance => lodash.fromPairs(balance))

              const getDebtPosition = () =>
                pool.methods
                  .debtPositionOf(from)
                  .call()
                  .then(debtPosition => ({
                    debtInUsd: formatUsd(debtPosition._debtInUsd),
                    depositInUsd: formatUsd(debtPosition._depositInUsd),
                    isHealthy: debtPosition._isHealthy,
                    issuableInUsd: formatUsd(debtPosition._issuableInUsd),
                    issuableLimitInUsd: formatUsd(
                      debtPosition._issuableLimitInUsd
                    )
                  }))

              const getDebt = () =>
                pool.methods
                  .debtOf(from)
                  .call()
                  .then(debt => formatUsd(debt._debtInUsd))

              const getDeposit = () =>
                pool.methods
                  .depositOf(from)
                  .call()
                  .then(deposit => ({
                    depositInUsd: formatUsd(deposit._depositInUsd),
                    issuableLimitInUsd: formatUsd(deposit._issuableLimitInUsd)
                  }))

              const getSwapAndCallbackLzArgs = (
                operation,
                sourceChainId,
                destinationChainId
              ) =>
                pool.methods
                  .poolRegistry()
                  .call()
                  .then(function (poolRegistryAddress) {
                    const poolRegistry = new web3.eth.Contract(
                      poolRegistryAbi,
                      poolRegistryAddress
                    )
                    return poolRegistry.methods
                      .quoter()
                      .call()
                      .then(function (quoterAddress) {
                        const quoterContract = new web3.eth.Contract(
                          quoterAbi,
                          quoterAddress
                        )
                        const method = {
                          flashRepay: 'getFlashRepaySwapAndCallbackLzArgs',
                          leverage: 'getLeverageSwapAndCallbackLzArgs'
                        }[operation]

                        return quoterContract.methods[method](
                          sourceChainId,
                          destinationChainId
                        ).call()
                      })
                  })

              const quoteCrossChainNativeFee = (
                operation,
                lzArgs,
                syntheticToken
              ) =>
                pool.methods
                  .poolRegistry()
                  .call()
                  .then(function (poolRegistryAddress) {
                    const poolRegistry = new web3.eth.Contract(
                      poolRegistryAbi,
                      poolRegistryAddress
                    )
                    const syntheticTokenContract = new web3.eth.Contract(
                      syntheticTokenAbi,
                      syntheticToken
                    )
                    return Promise.all([
                      poolRegistry.methods.quoter().call(),
                      syntheticTokenContract.methods.proxyOFT().call()
                    ]).then(function ([quoterAddress, proxyOFT]) {
                      const quoterContract = new web3.eth.Contract(
                        quoterAbi,
                        quoterAddress
                      )
                      const method = {
                        flashRepay: 'quoteCrossChainFlashRepayNativeFee',
                        leverage: 'quoteCrossChainLeverageNativeFee'
                      }[operation]

                      return quoterContract.methods[method](
                        proxyOFT,
                        lzArgs
                      ).call()
                    })
                  })

              const quoteCallbackNativeFee = (operation, srcChainId) =>
                pool.methods
                  .poolRegistry()
                  .call()
                  .then(function (poolRegistryAddress) {
                    const poolRegistry = new web3.eth.Contract(
                      poolRegistryAbi,
                      poolRegistryAddress
                    )
                    return Promise.all([
                      poolRegistry.methods.quoter().call()
                    ]).then(function ([quoterAddress]) {
                      const quoterContract = new web3.eth.Contract(
                        quoterAbi,
                        quoterAddress
                      )
                      const method = {
                        flashRepay: 'quoteFlashRepayCallbackNativeFee',
                        leverage: 'quoteLeverageCallbackNativeFee'
                      }[operation]

                      return quoterContract.methods[method](srcChainId).call()
                    })
                  })

              const parseSmartFarmingReceiptEvents = receipt =>
                pool.methods
                  .smartFarmingManager()
                  .call()
                  .then(smartFarmingManagerAddress =>
                    parseReceiptEvents(
                      smartFarmingManagerAbi,
                      smartFarmingManagerAddress,
                      receipt
                    )
                  )

              function parseCrossChainReceiptEvents(receipt) {
                // @TODO: try to use the regular parseReceiptEvents (multiple
                //  times with each of the contracts that may revert)
                const events = []

                if (receipt.logs) {
                  debug('Parsing logs into events')

                  receipt.events = {}

                  receipt.logs.forEach(function (log) {
                    log.returnValues = {}
                    log.signature = null
                    log.raw = {
                      data: log.data,
                      topics: log.topics
                    }
                    delete log.data
                    delete log.topics

                    const eventNumber = log.logIndex
                    receipt.events[eventNumber] = log
                  })

                  debug('Parsed %s logs', receipt.logs.length)
                  delete receipt.logs
                }

                debug('Parsing contract events')
                Object.keys(receipt.events).forEach(function (n) {
                  const event = receipt.events[n]

                  const descriptor = LayerZeroCustomEventsAbi.filter(
                    desc => desc.type === 'event'
                  )
                    .map(desc => ({
                      ...desc,
                      signature:
                        desc.signature || web3EthAbi.encodeEventSignature(desc)
                    }))
                    .find(desc => desc.signature === event.raw.topics[0])

                  if (descriptor) {
                    event.event = descriptor.name
                    event.signature = descriptor.signature
                    event.returnValues = web3EthAbi.decodeLog(
                      descriptor.inputs,
                      event.raw.data,
                      event.raw.topics.slice(1)
                    )
                  }
                  events.push(event)

                  debug('Decoded %s', event.event)

                  delete event.returnValues.__length__
                  delete receipt.events[n]
                })

                debug('Repopulating the events map')
                let count = 0
                events.forEach(function (ev) {
                  if (ev.event) {
                    if (receipt.events[ev.event]) {
                      if (Array.isArray(receipt.events[ev.event])) {
                        receipt.events[ev.event].push(ev)
                      } else {
                        receipt.events[ev.event] = [
                          receipt.events[ev.event],
                          ev
                        ]
                      }
                    } else {
                      receipt.events[ev.event] = ev
                    }
                  } else {
                    receipt.events[count] = ev
                    count++
                  }
                })

                return receipt
              }

              const decodeInputFromTransaction = (txHash, name) =>
                web3.eth
                  .getTransaction(txHash)
                  .then(tx =>
                    web3.eth.abi.decodeParameters(
                      smartFarmingManagerAbi.find(abi => abi.name === name)
                        .inputs,
                      tx.input.slice(10)
                    )
                  )

              const expectedGasFor = {
                approval: 47000,
                claim: 230000,
                deposit: 193000,
                flashRepay: 1250000,
                issue: 325000,
                leverage: 3570000,
                repay: 140000,
                retryCrossChainLeverageCallback: 376000, // @TODO: replace with real estimated gas
                retrySwapAndTriggerLeverageCallback: 376000, // @TODO: replace with real estimated gas
                swap: 388000,
                withdraw: 376000
              }

              const executeTransactions = createExecutor({
                chainId,
                from,
                overestimation: findByChainId(chainId).gasOverestimation,
                web3
              })

              const findReturnValue = (receipt, eventName, prop, address) =>
                []
                  .concat(receipt.events[eventName] || [])
                  .filter(
                    event =>
                      !address ||
                      event.address.toLowerCase() === address.toLowerCase()
                  )
                  .map(event => event.returnValues[prop])[0]

              const isApprovalNeeded = function (
                token,
                owner,
                spender,
                amount
              ) {
                const { decimals, symbol } =
                  findTokenByAddress(token, chainId) || {}

                debug(
                  'Checking if approval is needed for %s %s',
                  fromUnit(amount, decimals),
                  symbol
                )

                const contract = new web3.eth.Contract(erc20Abi, token)
                return contract.methods
                  .allowance(owner, spender)
                  .call()
                  .then(function (allowance) {
                    debug(
                      'Allowance for %s is %s',
                      symbol,
                      fromUnit(allowance, decimals)
                    )
                    const approvalNeeded = new Big(allowance).lt(amount)
                    debug(
                      'Approval is %s',
                      approvalNeeded ? 'needed' : 'not needed'
                    )
                    return approvalNeeded
                  })
              }

              const depositNativeToken = function (
                amount,
                transactionOptions = {}
              ) {
                debug(
                  'Initiating deposit of %s %s',
                  fromUnit(amount, nativeTokenDecimals),
                  nativeTokenSymbol
                )

                const depositToken = getDepositByCollateral(nativeTokenSymbol)
                let depositTokenDecimals
                let depositTokenSymbol

                const depositTokenContract = new web3.eth.Contract(
                  depositTokenAbi,
                  depositToken
                )
                const transactionsPromise = Promise.all([
                  depositTokenContract.methods.decimals().call(),
                  depositTokenContract.methods.symbol().call()
                ]).then(function ([
                  _depositTokenDecimals,
                  _depositTokenSymbol
                ]) {
                  depositTokenDecimals = _depositTokenDecimals
                  depositTokenSymbol = _depositTokenSymbol
                  return [
                    {
                      gas: expectedGasFor.deposit,
                      method: nativeTokenGateway.methods.deposit(poolAddress),
                      suffix: 'deposit',
                      value: amount
                    }
                  ]
                })

                const parseResults = function (transactionsData) {
                  const { receipt } =
                    transactionsData[transactionsData.length - 1]
                  parseReceiptEvents(depositTokenAbi, depositToken, receipt)
                  const sent = amount
                  const depositFee = findReturnValue(
                    receipt,
                    'CollateralDeposited',
                    'fee'
                  )
                  const received = findReturnValue(
                    receipt,
                    'CollateralDeposited',
                    'amount',
                    depositToken
                  )

                  debug(
                    'Deposit of %s %s completed',
                    fromUnit(amount, nativeTokenDecimals),
                    nativeTokenSymbol
                  )
                  debug(
                    'Received %s %s',
                    fromUnit(received, depositTokenDecimals),
                    depositTokenSymbol
                  )
                  return { depositFee, received, sent }
                }

                if (transactionOptions.encodeAbi) {
                  return transactionsPromise.then(encodeTxsAbi)
                }

                return executeTransactions(
                  transactionsPromise,
                  parseResults,
                  transactionOptions
                )
              }

              const deposit = function (
                token,
                amount,
                transactionOptions = {}
              ) {
                if (token === nativeTokenSymbol) {
                  return depositNativeToken(amount, transactionOptions)
                }
                const contract = new web3.eth.Contract(depositTokenAbi, token)
                const { decimals, symbol } = findTokenByAddress(token, chainId)
                debug(
                  'Initiating deposit of %s %s',
                  fromUnit(amount, decimals),
                  symbol
                )
                const owner = transactionOptions.from || from
                const depositToken = getDepositByCollateral(token)
                let depositTokenDecimals
                let depositTokenSymbol

                const depositTokenContract = new web3.eth.Contract(
                  depositTokenAbi,
                  depositToken
                )
                const transactionsPromise = Promise.all([
                  depositTokenContract.methods.decimals().call(),
                  depositTokenContract.methods.symbol().call()
                ]).then(([_depositTokenDecimals, _depositTokenSymbol]) =>
                  isApprovalNeeded(token, owner, depositToken, amount).then(
                    function (approvalNeeded) {
                      depositTokenDecimals = _depositTokenDecimals
                      depositTokenSymbol = _depositTokenSymbol

                      const txs = []
                      if (approvalNeeded) {
                        txs.push({
                          gas: expectedGasFor.approval,
                          method: contract.methods.approve(
                            depositToken,
                            amount
                          ),
                          suffix: 'approve'
                        })
                      }
                      txs.push({
                        gas: expectedGasFor.deposit,
                        method: depositTokenContract.methods.deposit(
                          amount,
                          owner
                        ),
                        suffix: 'deposit'
                      })
                      return txs
                    }
                  )
                )

                const parseResults = function (transactionsData) {
                  const { receipt } =
                    transactionsData[transactionsData.length - 1]
                  const sent = amount
                  const depositFee = findReturnValue(
                    receipt,
                    'CollateralDeposited',
                    'fee'
                  )
                  const received = findReturnValue(
                    receipt,
                    'CollateralDeposited',
                    'amount',
                    depositToken
                  )

                  debug(
                    'Deposit of %s %s completed',
                    fromUnit(amount, decimals),
                    symbol
                  )
                  debug(
                    'Received %s %s',
                    fromUnit(received, depositTokenDecimals),
                    depositTokenSymbol
                  )

                  return { depositFee, received, sent }
                }

                if (transactionOptions.encodeAbi) {
                  return transactionsPromise.then(encodeTxsAbi)
                }

                return executeTransactions(
                  transactionsPromise,
                  parseResults,
                  transactionOptions
                )
              }

              const approveAndDeposit = function (
                token,
                approveAmount,
                depositAmount,
                transactionOptions = {}
              ) {
                const contract = new web3.eth.Contract(depositTokenAbi, token)
                const { decimals, symbol } = findTokenByAddress(token, chainId)
                debug(
                  'Initiating approval of %s and deposit of %s %s',
                  fromUnit(approveAmount, decimals),
                  fromUnit(depositAmount, decimals),
                  symbol
                )
                const owner = transactionOptions.from || from

                const depositToken = getDepositByCollateral(token)
                let depositTokenDecimals
                let depositTokenSymbol
                const depositTokenContract = new web3.eth.Contract(
                  depositTokenAbi,
                  depositToken
                )
                const transactionsPromise = Promise.all([
                  depositTokenContract.methods.decimals().call(),
                  depositTokenContract.methods.symbol().call()
                ]).then(function ([
                  _depositTokenDecimals,
                  _depositTokenSymbol
                ]) {
                  depositTokenDecimals = _depositTokenDecimals
                  depositTokenSymbol = _depositTokenSymbol

                  const txs = []
                  txs.push(
                    {
                      gas: expectedGasFor.approval,
                      method: contract.methods.approve(
                        depositToken,
                        approveAmount
                      ),
                      suffix: 'approve'
                    },
                    {
                      gas: expectedGasFor.deposit,
                      method: depositTokenContract.methods.deposit(
                        depositAmount,
                        owner
                      ),
                      suffix: 'deposit'
                    }
                  )
                  return txs
                })

                const parseResults = function (transactionsData) {
                  const { receipt } =
                    transactionsData[transactionsData.length - 1]
                  const sent = depositAmount
                  const received = findReturnValue(
                    receipt,
                    'CollateralDeposited',
                    'amount',
                    depositToken
                  )

                  debug(
                    'Approval of %s and deposit of %s %s completed',
                    fromUnit(approveAmount, decimals),
                    fromUnit(depositAmount, decimals),
                    symbol
                  )
                  debug(
                    'Received %s %s',
                    fromUnit(received, depositTokenDecimals),
                    depositTokenSymbol
                  )

                  return { received, sent }
                }

                return executeTransactions(
                  transactionsPromise,
                  parseResults,
                  transactionOptions
                )
              }

              const withdrawNativeToken = function (
                amount,
                transactionOptions = {}
              ) {
                debug(
                  'Initiating withdraw of %s %s',
                  fromUnit(amount, nativeTokenDecimals),
                  nativeTokenSymbol
                )

                const owner = transactionOptions.from || from

                const depositToken = getDepositByCollateral(nativeTokenSymbol)
                let depositTokenDecimals
                let depositTokenSymbol

                const depositTokenContract = new web3.eth.Contract(
                  depositTokenAbi,
                  depositToken
                )
                const transactionsPromise = Promise.all([
                  depositTokenContract.methods.decimals().call(),
                  depositTokenContract.methods.symbol().call()
                ]).then(([_depositTokenDecimals, _depositTokenSymbol]) =>
                  isApprovalNeeded(
                    depositToken,
                    owner,
                    nativeTokenGatewayAddress,
                    amount
                  ).then(function (approvalNeeded) {
                    depositTokenDecimals = _depositTokenDecimals
                    depositTokenSymbol = _depositTokenSymbol

                    debug(
                      'Sending %s %s',
                      fromUnit(amount, depositTokenDecimals),
                      depositTokenSymbol
                    )
                    const txs = []
                    if (approvalNeeded) {
                      txs.push({
                        gas: expectedGasFor.approval,
                        method: depositTokenContract.methods.approve(
                          nativeTokenGatewayAddress,
                          amount
                        ),
                        suffix: 'approve'
                      })
                    }
                    txs.push({
                      gas: expectedGasFor.withdraw,
                      method: nativeTokenGateway.methods.withdraw(
                        poolAddress,
                        amount
                      ),
                      suffix: 'withdraw'
                    })
                    return txs
                  })
                )

                const parseResults = function (transactionsData) {
                  const { receipt } =
                    transactionsData[transactionsData.length - 1]
                  parseReceiptEvents(depositTokenAbi, depositToken, receipt)
                  const sent = findReturnValue(
                    receipt,
                    'Transfer',
                    'value',
                    depositToken
                  )
                  const received = findReturnValue(
                    receipt,
                    'CollateralWithdrawn',
                    'amount',
                    depositToken
                  )

                  // modify event to match destination and from accounts, instead of gateway
                  receipt.events.CollateralWithdrawn.returnValues.account =
                    transactionOptions.from || from

                  debug(
                    'Withdrawal of %s %s completed',
                    fromUnit(amount, nativeTokenDecimals),
                    nativeTokenSymbol
                  )
                  debug(
                    'Sent %s %s',
                    fromUnit(sent, depositTokenDecimals),
                    depositTokenSymbol
                  )
                  debug(
                    'Received %s %s',
                    fromUnit(received, nativeTokenDecimals),
                    nativeTokenSymbol
                  )

                  return { receipt, received, sent }
                }

                if (transactionOptions.encodeAbi) {
                  return transactionsPromise.then(encodeTxsAbi)
                }

                return executeTransactions(
                  transactionsPromise,
                  parseResults,
                  transactionOptions
                )
              }

              const withdraw = function (
                token,
                amount,
                transactionOptions = {}
              ) {
                if (token === nativeTokenSymbol) {
                  return withdrawNativeToken(amount, transactionOptions)
                }
                const { decimals, symbol } = findTokenByAddress(token, chainId)
                const owner = transactionOptions.from || from
                debug(
                  'Initiating withdrawal of %s %s',
                  fromUnit(amount, decimals),
                  symbol
                )

                const depositToken = getDepositByCollateral(token)
                let depositTokenDecimals
                let depositTokenSymbol

                const depositTokenContract = new web3.eth.Contract(
                  depositTokenAbi,
                  depositToken
                )
                const transactionsPromise = Promise.all([
                  depositTokenContract.methods.decimals().call(),
                  depositTokenContract.methods.symbol().call()
                ]).then(function ([
                  _depositTokenDecimals,
                  _depositTokenSymbol
                ]) {
                  depositTokenDecimals = _depositTokenDecimals
                  depositTokenSymbol = _depositTokenSymbol

                  debug(
                    'Sending %s %s',
                    fromUnit(amount, depositTokenDecimals),
                    depositTokenSymbol
                  )

                  return [
                    {
                      gas: expectedGasFor.withdraw,
                      method: depositTokenContract.methods.withdraw(
                        amount,
                        owner
                      ),
                      suffix: 'withdraw'
                    }
                  ]
                })

                const parseResults = function ([transactionData]) {
                  const { receipt } = transactionData
                  const sent = findReturnValue(
                    receipt,
                    'Transfer',
                    'value',
                    depositToken
                  )
                  const received = findReturnValue(
                    receipt,
                    'CollateralWithdrawn',
                    'amount',
                    depositToken
                  )

                  debug(
                    'Withdrawal of %s %s completed',
                    fromUnit(amount, decimals),
                    symbol
                  )
                  debug(
                    'Sent %s %s',
                    fromUnit(sent, depositTokenDecimals),
                    depositTokenSymbol
                  )
                  debug('Received %s %s', fromUnit(received, decimals), symbol)

                  return { receipt, received, sent }
                }

                if (transactionOptions.encodeAbi) {
                  return transactionsPromise.then(encodeTxsAbi)
                }

                return executeTransactions(
                  transactionsPromise,
                  parseResults,
                  transactionOptions
                )
              }

              const issue = function (token, amount, transactionOptions = {}) {
                const syntheticTokenContract = new web3.eth.Contract(
                  syntheticTokenAbi,
                  token
                )
                const owner = transactionOptions.from || from

                let debtToken
                let syntheticTokenDecimals
                let syntheticTokenSymbol

                const transactionsPromise = Promise.all([
                  pool.methods.debtTokenOf(token).call(),
                  syntheticTokenContract.methods.decimals().call(),
                  syntheticTokenContract.methods.symbol().call()
                ]).then(function ([
                  _debtToken,
                  _syntheticTokenDecimals,
                  _syntheticTokenSymbol
                ]) {
                  debtToken = _debtToken
                  syntheticTokenDecimals = _syntheticTokenDecimals
                  syntheticTokenSymbol = _syntheticTokenSymbol

                  const debtTokenContract = new web3.eth.Contract(
                    debtTokenAbi,
                    debtToken
                  )

                  debug(
                    'Initiating issue of %s %s',
                    fromUnit(amount, syntheticTokenDecimals),
                    syntheticTokenSymbol
                  )

                  return [
                    {
                      gas: expectedGasFor.issue,
                      method: debtTokenContract.methods.issue(amount, owner),
                      suffix: 'issue'
                    }
                  ]
                })
                const parseResults = function (transactionsData) {
                  const { receipt } =
                    transactionsData[transactionsData.length - 1]
                  const sent = amount
                  const received = findReturnValue(
                    receipt,
                    'Transfer',
                    'value',
                    token
                  )
                  const issueFee = findReturnValue(
                    receipt,
                    'SyntheticTokenIssued',
                    'fee'
                  )

                  debug(
                    'Issue of %s %s completed',
                    fromUnit(amount, syntheticTokenDecimals),
                    syntheticTokenSymbol
                  )
                  debug(
                    'Received %s %s',
                    fromUnit(received, syntheticTokenDecimals),
                    syntheticTokenSymbol
                  )

                  return { issueFee, received, sent }
                }

                if (transactionOptions.encodeAbi) {
                  return transactionsPromise.then(encodeTxsAbi)
                }

                return executeTransactions(
                  transactionsPromise,
                  parseResults,
                  transactionOptions
                )
              }

              const repay = function (token, amount, transactionOptions = {}) {
                const syntheticTokenContract = new web3.eth.Contract(
                  syntheticTokenAbi,
                  token
                )
                const owner = transactionOptions.from || from

                let debtToken
                let syntheticTokenDecimals
                let syntheticTokenSymbol

                const transactionsPromise = Promise.all([
                  pool.methods.debtTokenOf(token).call(),
                  syntheticTokenContract.methods.decimals().call(),
                  syntheticTokenContract.methods.symbol().call()
                ]).then(function ([
                  _debtToken,
                  _syntheticTokenDecimals,
                  _syntheticTokenSymbol
                ]) {
                  debtToken = _debtToken
                  syntheticTokenDecimals = _syntheticTokenDecimals
                  syntheticTokenSymbol = _syntheticTokenSymbol

                  const debtTokenContract = new web3.eth.Contract(
                    debtTokenAbi,
                    debtToken
                  )

                  debug(
                    'Initiating repay of %s %s',
                    fromUnit(amount, syntheticTokenDecimals),
                    syntheticTokenSymbol
                  )

                  return [
                    {
                      gas: expectedGasFor.repay,
                      method: debtTokenContract.methods.repay(owner, amount),
                      suffix: 'repay'
                    }
                  ]
                })
                const parseResults = function (transactionsData) {
                  const { receipt } =
                    transactionsData[transactionsData.length - 1]
                  const sent = amount

                  const fee = findReturnValue(
                    receipt,
                    'DebtRepaid',
                    'fee',
                    debtToken
                  )

                  const repaid = findReturnValue(
                    receipt,
                    'DebtRepaid',
                    'repaid',
                    debtToken
                  )

                  debug(
                    'Repay of %s %s + fee of %s %s completed',
                    fromUnit(repaid, syntheticTokenDecimals),
                    syntheticTokenSymbol,
                    fromUnit(fee, syntheticTokenDecimals),
                    syntheticTokenSymbol
                  )

                  return { fee, repaid, sent }
                }

                if (transactionOptions.encodeAbi) {
                  return transactionsPromise.then(encodeTxsAbi)
                }

                return executeTransactions(
                  transactionsPromise,
                  parseResults,
                  transactionOptions
                )
              }

              const repayAll = function (token, transactionOptions = {}) {
                const syntheticTokenContract = new web3.eth.Contract(
                  syntheticTokenAbi,
                  token
                )
                const owner = transactionOptions.from || from

                let debtToken
                let syntheticTokenDecimals
                let syntheticTokenSymbol

                const transactionsPromise = Promise.all([
                  pool.methods.debtTokenOf(token).call(),
                  syntheticTokenContract.methods.decimals().call(),
                  syntheticTokenContract.methods.symbol().call()
                ]).then(function ([
                  _debtToken,
                  _syntheticTokenDecimals,
                  _syntheticTokenSymbol
                ]) {
                  debtToken = _debtToken
                  syntheticTokenDecimals = _syntheticTokenDecimals
                  syntheticTokenSymbol = _syntheticTokenSymbol

                  const debtTokenContract = new web3.eth.Contract(
                    debtTokenAbi,
                    debtToken
                  )

                  debug('Initiating full repay of %s', syntheticTokenSymbol)

                  return [
                    {
                      gas: expectedGasFor.repay,
                      method: debtTokenContract.methods.repayAll(owner),
                      suffix: 'repay'
                    }
                  ]
                })
                const parseResults = function (transactionsData) {
                  const { receipt } =
                    transactionsData[transactionsData.length - 1]

                  const sent = findReturnValue(
                    receipt,
                    'DebtRepaid',
                    'amount',
                    debtToken
                  )

                  const fee = findReturnValue(
                    receipt,
                    'DebtRepaid',
                    'fee',
                    debtToken
                  )

                  const repaid = findReturnValue(
                    receipt,
                    'DebtRepaid',
                    'repaid',
                    debtToken
                  )

                  debug(
                    'Repay of %s %s + fee of %s %s completed',
                    fromUnit(repaid, syntheticTokenDecimals),
                    syntheticTokenSymbol,
                    fromUnit(fee, syntheticTokenDecimals),
                    syntheticTokenSymbol
                  )

                  return { fee, repaid, sent }
                }
                return executeTransactions(
                  transactionsPromise,
                  parseResults,
                  transactionOptions
                )
              }

              const swap = function (
                synthTokenIn,
                synthTokenOut,
                amount,
                transactionOptions = {}
              ) {
                const syntheticTokenInContract = new web3.eth.Contract(
                  syntheticTokenAbi,
                  synthTokenIn
                )
                const syntheticTokenOutContract = new web3.eth.Contract(
                  syntheticTokenAbi,
                  synthTokenOut
                )

                let synthTokenInDecimals
                let synthTokenInSymbol
                let synthTokenOutDecimals
                let synthTokenOutSymbol

                const transactionsPromise = Promise.all([
                  syntheticTokenInContract.methods.decimals().call(),
                  syntheticTokenInContract.methods.symbol().call(),
                  syntheticTokenOutContract.methods.decimals().call(),
                  syntheticTokenOutContract.methods.symbol().call()
                ]).then(function ([
                  _synthTokenInDecimals,
                  _synthTokenInSymbol,
                  _synthTokenOutDecimals,
                  _synthTokenOutSymbol
                ]) {
                  synthTokenInDecimals = _synthTokenInDecimals
                  synthTokenInSymbol = _synthTokenInSymbol
                  synthTokenOutDecimals = _synthTokenOutDecimals
                  synthTokenOutSymbol = _synthTokenOutSymbol

                  debug(
                    'Initiating swap of %s from %s to %s',
                    fromUnit(amount, synthTokenInDecimals),
                    synthTokenInSymbol,
                    synthTokenOutSymbol
                  )

                  return [
                    {
                      gas: expectedGasFor.swap,
                      method: pool.methods.swap(
                        synthTokenIn,
                        synthTokenOut,
                        amount
                      ),
                      suffix: 'swap'
                    }
                  ]
                })

                const parseResults = function (transactionsData) {
                  const { receipt } =
                    transactionsData[transactionsData.length - 1]
                  const sent = amount
                  const received = findReturnValue(
                    receipt,
                    'SyntheticTokenSwapped',
                    'amountOut',
                    poolAddress
                  )

                  debug(
                    'Swap of %s from %s to %s completed',
                    fromUnit(amount, synthTokenInDecimals),
                    synthTokenInSymbol,
                    synthTokenOutSymbol
                  )
                  debug(
                    'Received %s %s',
                    fromUnit(received, synthTokenOutDecimals),
                    synthTokenOutSymbol
                  )
                  return { received, sent }
                }
                return executeTransactions(
                  transactionsPromise,
                  parseResults,
                  transactionOptions
                )
              }

              const getSwapFeeDiscountByEsMETBalance = function (esMETBalance) {
                const bigEsMETBalance = Big(toUnit(esMETBalance))
                let discount = 0
                for (let i = 0; i < tiers.length; i++) {
                  const [min, _discount] = tiers[i]
                  if (bigEsMETBalance.gte(min)) {
                    discount = fromUnit(_discount)
                  }
                }
                return discount
              }

              const getSwapFeeFor = address =>
                feeProvider.methods.swapFeeFor(address).call().then(fromUnit)

              // Resilient across contract versions:
              // - v1.3.2+ charges a swap fee per synth-pair, keyed on the
              //   (synthIn, synthOut) tokens rather than the user account, via
              //   `swapFees(synthIn, synthOut)`.
              // - older versions charge a per-user fee via `swapFeeFor(account)`.
              const getSwapFee = function ({
                account,
                synthIn,
                synthOut
              } = {}) {
                if (feeProvider.methods.swapFees) {
                  if (!synthIn || !synthOut) {
                    return Promise.resolve('0')
                  }
                  return feeProvider.methods
                    .swapFees(synthIn, synthOut)
                    .call()
                    .then(fromUnit)
                }
                return feeProvider.methods
                  .swapFeeFor(account)
                  .call()
                  .then(fromUnit)
              }

              const leverage = function ({
                _leverage,
                amount,
                collateralToken,
                depositToken,
                mainCollateral,
                slippage,
                syntheticToken,
                transactionOptions = {}
              }) {
                const getDepositAmountMin = function () {
                  const calculateDepositAmountMin = _amount =>
                    amountMinFromSlippage(
                      Big(_amount).times(_leverage),
                      slippage
                    )
                  if (mainCollateral === collateralToken) {
                    return Promise.resolve(calculateDepositAmountMin(amount))
                  }
                  return masterOracle
                    .quote(collateralToken, mainCollateral, amount)
                    .then(calculateDepositAmountMin)
                }

                const contract = new web3.eth.Contract(
                  depositTokenAbi,
                  collateralToken
                )
                const { decimals, symbol } = findTokenByAddress(
                  collateralToken,
                  chainId
                )
                debug(
                  'Initiating deposit of %s %s with leverage of %s completed',
                  fromUnit(amount, decimals),
                  symbol,
                  _leverage
                )
                const owner = transactionOptions.from || from

                const transactionsPromise = pool.methods
                  .smartFarmingManager()
                  .call()
                  .then(smartFarmingManagerAddress =>
                    Promise.all([
                      isApprovalNeeded(
                        collateralToken,
                        owner,
                        smartFarmingManagerAddress,
                        amount
                      ),
                      getDepositAmountMin()
                    ]).then(function ([approvalNeeded, depositAmountMin]) {
                      const txs = []
                      if (approvalNeeded) {
                        txs.push({
                          gas: expectedGasFor.approval,
                          method: contract.methods.approve(
                            smartFarmingManagerAddress,
                            Big(amount).toFixed()
                          ),
                          suffix: 'approve'
                        })
                      }
                      const smartFarmingManagerContract = new web3.eth.Contract(
                        smartFarmingManagerAbi,
                        smartFarmingManagerAddress
                      )
                      txs.push({
                        gas: expectedGasFor.leverage,
                        method: smartFarmingManagerContract.methods.leverage(
                          collateralToken,
                          depositToken,
                          syntheticToken,
                          Big(amount).toFixed(),
                          Big(_leverage).times(1e18).toFixed(),
                          depositAmountMin
                        ),
                        suffix: 'leverage'
                      })

                      return txs
                    })
                  )

                const parseResults = function ([transactionData]) {
                  const { receipt } = transactionData
                  debug(
                    'Deposit of %s %s with leverage of %s completed',
                    fromUnit(amount, decimals),
                    symbol,
                    _leverage
                  )
                  return { receipt }
                }

                return executeTransactions(
                  transactionsPromise,
                  parseResults,
                  transactionOptions
                )
              }

              const flashRepay = function ({
                collateralToken,
                syntheticToken,
                depositToken,
                withdrawAmount,
                repayAmount,
                slippage = 0,
                transactionOptions = {}
              }) {
                const contract = new web3.eth.Contract(
                  depositTokenAbi,
                  collateralToken
                )
                const { decimals, symbol } = findTokenByAddress(
                  collateralToken,
                  chainId
                )
                debug(
                  'Initiating flash repay of %s %s',
                  fromUnit(withdrawAmount, decimals),
                  symbol
                )
                const owner = transactionOptions.from || from

                const method = function (address) {
                  const smartFarmingManagerContract = new web3.eth.Contract(
                    smartFarmingManagerAbi,
                    address
                  )
                  return smartFarmingManagerContract.methods.flashRepay(
                    syntheticToken,
                    depositToken,
                    Big(withdrawAmount).toFixed(),
                    amountMinFromSlippage(repayAmount, slippage)
                  )
                }

                const { dryRun, ...txOptions } = transactionOptions

                if (dryRun) {
                  return pool.methods
                    .smartFarmingManager()
                    .call()
                    .then(smartFarmingManagerAddress =>
                      method(smartFarmingManagerAddress).call({
                        from: owner
                      })
                    )
                }

                const transactionsPromise = Promise.all([
                  isApprovalNeeded(
                    collateralToken,
                    owner,
                    poolAddress,
                    withdrawAmount
                  ),
                  pool.methods.smartFarmingManager().call()
                ]).then(function ([
                  approvalNeeded,
                  smartFarmingManagerAddress
                ]) {
                  const txs = []
                  if (approvalNeeded) {
                    txs.push({
                      gas: expectedGasFor.approval,
                      method: contract.methods.approve(
                        poolAddress,
                        Big(withdrawAmount).toFixed()
                      ),
                      suffix: 'approve'
                    })
                  }

                  txs.push({
                    gas: expectedGasFor.flashRepay,
                    method: method(smartFarmingManagerAddress),
                    suffix: 'flash-repay'
                  })
                  return txs
                })

                const parseResults = function ([transactionData]) {
                  const { receipt } = transactionData
                  debug(
                    'Flash repay of %s %s completed',
                    fromUnit(withdrawAmount, decimals),
                    symbol
                  )
                  return { receipt }
                }

                return executeTransactions(
                  transactionsPromise,
                  parseResults,
                  txOptions
                )
              }

              const claimableMerkle = rewardsDistributor =>
                Promise.all([
                  getMerkleProofFile(rewardsDistributor, options),
                  rewardsDistributor.methods.claimed(from).call()
                ]).then(([proofsFileData, claimed]) =>
                  proofsFileData[from]
                    ? Big(proofsFileData[from].accumulated)
                        .minus(claimed)
                        .toFixed()
                    : undefined
                )

              const claimablePool = (rewardsDistributor, token) =>
                token
                  ? rewardsDistributor.methods.claimable(from, token).call()
                  : rewardsDistributor.methods.claimable(from).call()

              const claimable = function (token) {
                const _rewardsDistributors = token
                  ? rewardsDistributors.filter(
                      ({ rewardToken }) => rewardToken === token
                    )
                  : rewardsDistributors
                return Promise.all(
                  _rewardsDistributors.map(function ({
                    address,
                    rewardToken: rewardTokenAddress,
                    rewardsDistributor,
                    type
                  }) {
                    const rewardToken = findTokenByAddress(
                      rewardTokenAddress,
                      chainId
                    )

                    const rewardTokenContract = new web3.eth.Contract(
                      erc20Abi,
                      rewardTokenAddress
                    )

                    return Promise.all([
                      rewardTokenContract.methods.balanceOf(address).call(),
                      rewardToken.symbol === 'MET'
                        ? getMETPrice()
                        : convertToUsd(
                            rewardTokenAddress,
                            toUnit(1, rewardToken.decimals)
                          ).then(value => fromUnit(value, rewardToken.decimals))
                    ])
                      .then(function ([rewardDistributorBalance, usdPrice]) {
                        const claimableMethod = {
                          merkle: claimableMerkle,
                          pool: claimablePool
                        }[type]

                        return claimableMethod(rewardsDistributor).then(
                          function (claimableAmount) {
                            if (Big(claimableAmount).gt(0)) {
                              const claimableData = {
                                amount: claimableAmount,
                                rewardDistributorBalance,
                                rewardToken
                              }
                              return {
                                ...claimableData,
                                usdPrice
                              }
                            }
                            return undefined
                          }
                        )
                      })
                      .catch(console.error)
                  })
                ).then(claimables => claimables.filter(c => !!c))
              }

              const claimRewardsForPoolRD = (rewardsDistributor, owner) => ({
                gas: expectedGasFor.claim,
                method: rewardsDistributor.methods.claimRewards(owner),
                suffix: 'claim'
              })

              const claimRewardsForMerkleRD = function (
                rewardsDistributor,
                owner,
                amount
              ) {
                if (!amount) {
                  throw new Error('Amount is required to claim')
                }
                return getMerkleProofFile(rewardsDistributor, options).then(
                  proofFile => ({
                    gas: expectedGasFor.claim, // @TODO: check gas for claiming with merkle
                    method: rewardsDistributor.methods.claim(
                      amount,
                      proofFile[owner].proof
                    ),
                    suffix: 'claim'
                  })
                )
              }

              const claimRewards = function (
                amounts = {},
                transactionOptions = {}
              ) {
                const tokens = Object.keys(amounts)
                const _rewardsDistributors = (
                  tokens.length > 0
                    ? rewardsDistributors.filter(({ rewardToken }) =>
                        tokens.includes(rewardToken)
                      )
                    : rewardsDistributors
                ).map(function (rd) {
                  const rewardToken = findTokenByAddress(
                    rd.rewardToken,
                    chainId
                  )
                  return { ...rd, rewardToken }
                })

                const transactionsPromise = Promise.all(
                  _rewardsDistributors.map(function ({
                    rewardsDistributor,
                    rewardToken: { address, symbol },
                    type
                  }) {
                    const owner = transactionOptions.from || from
                    debug('Initiating claim of %s', symbol)

                    const claimMethod = {
                      merkle: claimRewardsForMerkleRD,
                      pool: claimRewardsForPoolRD
                    }[type]

                    return claimMethod(
                      rewardsDistributor,
                      owner,
                      amounts[address]
                    )
                  })
                )

                const parseResults = function ([transactionData]) {
                  const { receipt } = transactionData
                  const claimedTokens = _rewardsDistributors.map(
                    ({ rewardToken }) => rewardToken.symbol
                  )
                  debug('Claim of %s completed', claimedTokens.join(' ,'))
                  return { receipt }
                }

                return executeTransactions(
                  transactionsPromise,
                  parseResults,
                  transactionOptions
                )
              }

              const crossChainLeverage = function ({
                _leverage,
                amount,
                collateralToken,
                depositToken,
                lzArgs,
                bridgeToken,
                nativeFee,
                mainCollateral,
                slippage,
                syntheticToken,
                transactionOptions = {}
              }) {
                const getDepositAmountMin = function () {
                  const calculateDepositAmountMin = _amount =>
                    amountMinFromSlippage(
                      Big(_amount).times(_leverage),
                      slippage
                    )
                  if (mainCollateral === collateralToken) {
                    return Promise.resolve(calculateDepositAmountMin(amount))
                  }
                  return masterOracle
                    .quote(collateralToken, mainCollateral, amount)
                    .then(calculateDepositAmountMin)
                }

                const getSwapAmountMin = function () {
                  const calculateSwapAmountMin = _amount =>
                    amountMinFromSlippage(
                      Big(_amount).times(_leverage),
                      slippage
                    )
                  return masterOracle
                    .quote(syntheticToken, bridgeToken, amount)
                    .then(calculateSwapAmountMin)
                }

                const contract = new web3.eth.Contract(
                  depositTokenAbi,
                  collateralToken
                )
                const { decimals, symbol } = findTokenByAddress(
                  collateralToken,
                  chainId
                )
                debug(
                  'Initiating deposit of %s %s with cross chain leverage of %s completed',
                  fromUnit(amount, decimals),
                  symbol,
                  _leverage
                )
                const owner = transactionOptions.from || from

                const transactionsPromise = pool.methods
                  .smartFarmingManager()
                  .call()
                  .then(smartFarmingManagerAddress =>
                    Promise.all([
                      isApprovalNeeded(
                        collateralToken,
                        owner,
                        smartFarmingManagerAddress,
                        amount
                      ),
                      getDepositAmountMin(),
                      getSwapAmountMin()
                    ]).then(function ([
                      approvalNeeded,
                      depositAmountMin,
                      swapAmountMin
                    ]) {
                      const txs = []
                      if (approvalNeeded) {
                        txs.push({
                          gas: expectedGasFor.approval,
                          method: contract.methods.approve(
                            smartFarmingManagerAddress,
                            Big(amount).toFixed()
                          ),
                          suffix: 'approve'
                        })
                      }
                      const smartFarmingManagerContract = new web3.eth.Contract(
                        smartFarmingManagerAbi,
                        smartFarmingManagerAddress
                      )
                      txs.push({
                        gas: expectedGasFor.leverage,
                        method:
                          smartFarmingManagerContract.methods.crossChainLeverage(
                            collateralToken,
                            syntheticToken,
                            bridgeToken,
                            depositToken,
                            Big(amount).toFixed(),
                            Big(_leverage).times(1e18).toFixed(),
                            swapAmountMin,
                            depositAmountMin,
                            lzArgs
                          ),
                        suffix: 'crossChainLeverage',
                        value: nativeFee
                      })

                      return txs
                    })
                  )

                const parseResults = function ([transactionData]) {
                  const { receipt } = transactionData
                  debug(
                    'Deposit of %s %s with cross chain leverage of %s completed',
                    fromUnit(amount, decimals),
                    symbol,
                    _leverage
                  )
                  return { receipt }
                }

                return executeTransactions(
                  transactionsPromise,
                  parseResults,
                  transactionOptions
                )
              }

              /**
               * Retry cross-chain leverage operation that failed on tx2
               * @param srcChainId_
               * @param srcAddress_
               * @param nonce_
               * @param amount_
               * @param payload_
               * @param newAmountOutMin_
               * @param nativeFee
               * @param transactionOptions
               * @returns {{promise: *, emitter: *}}
               */
              function retrySwapAndTriggerLeverageCallback(
                {
                  srcChainId_,
                  srcAddress_,
                  nonce_,
                  amount_,
                  payload_,
                  newAmountOutMin_,
                  nativeFee
                },
                transactionOptions
              ) {
                const transactionsPromise = pool.methods
                  .poolRegistry()
                  .call()
                  .then(function (poolRegistryAddress) {
                    const poolRegistry = new web3.eth.Contract(
                      poolRegistryAbi,
                      poolRegistryAddress
                    )

                    return poolRegistry.methods
                      .crossChainDispatcher()
                      .call()
                      .then(function (crossChainDispatcherAddress) {
                        const crossChainDispatcherContract =
                          new web3.eth.Contract(
                            crossChainDispatcherAbi,
                            crossChainDispatcherAddress
                          )

                        return [
                          {
                            gas: expectedGasFor.retrySwapAndTriggerLeverageCallback,
                            method:
                              crossChainDispatcherContract.methods.retrySwapAndTriggerLeverageCallback(
                                srcChainId_,
                                srcAddress_,
                                nonce_,
                                amount_,
                                payload_,
                                newAmountOutMin_
                              ),
                            suffix: 'retry-cc-leverage',
                            value: nativeFee
                          }
                        ]
                      })
                  })

                const parseResults = function ([transactionData]) {
                  const { receipt } = transactionData
                  debug('Retried deposit with cross chain leverage completed')
                  return { receipt }
                }

                return executeTransactions(
                  transactionsPromise,
                  parseResults,
                  transactionOptions
                )
              }

              /**
               * Retry cross-chain leverage operation that failed on tx3
               * @param srcChainId_
               * @param srcAddress_
               * @param nonce_
               * @param dstPoolId
               * @param amount_
               * @param payload_
               * @param newDepositAmountMin_
               * @param transactionOptions
               * @returns {{promise: *, emitter: *}}
               */
              const retryCrossChainLeverageCallback = function (
                {
                  srcChainId_,
                  srcAddress_,
                  nonce_,
                  dstPoolId,
                  amount_,
                  payload_: encodedPayload,
                  newDepositAmountMin_
                },
                transactionOptions
              ) {
                const transactionsPromise = Promise.all([
                  pool.methods.poolRegistry().call(),
                  pool.methods.smartFarmingManager().call()
                ]).then(function ([
                  poolRegistryAddress,
                  smartFarmingManagerAddress
                ]) {
                  const poolRegistry = new web3.eth.Contract(
                    poolRegistryAbi,
                    poolRegistryAddress
                  )
                  const smartFarmingManager = new web3.eth.Contract(
                    smartFarmingManagerAbi,
                    smartFarmingManagerAddress
                  )
                  return poolRegistry.methods
                    .crossChainDispatcher()
                    .call()
                    .then(function (crossChainDispatcherAddress) {
                      const crossChainDispatcher = new web3.eth.Contract(
                        crossChainDispatcherAbi,
                        crossChainDispatcherAddress
                      )
                      return crossChainDispatcher.methods
                        .stargateComposer()
                        .call()
                    })
                    .then(function (stargateComposerAddress) {
                      const stargateComponser = new web3.eth.Contract(
                        stargateComposerAbi,
                        stargateComposerAddress
                      )
                      return stargateComponser.methods.factory().call()
                    })
                    .then(function (stargateFactoryAddress) {
                      const stargateFactory = new web3.eth.Contract(
                        stargateFactoryAbi,
                        stargateFactoryAddress
                      )
                      return stargateFactory.methods.getPool(dstPoolId).call()
                    })
                    .then(function (stargatePoolAddress) {
                      const stargatePool = new web3.eth.Contract(
                        stargatePoolAbi,
                        stargatePoolAddress
                      )
                      return stargatePool.methods.token().call()
                    })
                    .then(function (token_) {
                      const { payload } = decodeSgSwapEvents(encodedPayload)
                      return [
                        {
                          gas: expectedGasFor.retryCrossChainLeverageCallback,
                          method:
                            smartFarmingManager.methods.retryCrossChainLeverageCallback(
                              srcChainId_,
                              srcAddress_,
                              nonce_,
                              token_,
                              amount_,
                              payload,
                              newDepositAmountMin_
                            ),
                          suffix: 'retry-cc-leverage'
                        }
                      ]
                    })
                })

                const parseResults = function ([transactionData]) {
                  const { receipt } = transactionData
                  debug('Retried deposit with cross chain leverage completed')
                  return { receipt }
                }

                return executeTransactions(
                  transactionsPromise,
                  parseResults,
                  transactionOptions
                )
              }

              const crossChainFlashRepay = function ({
                bridgeToken,
                collateralToken,
                depositToken,
                repayAmount,
                slippage = 0,
                syntheticToken,
                transactionOptions = {},
                withdrawAmount
              }) {
                // Get the minimum output from a swap considering slippage.
                // @TODO: We'll be using a single slippage value for sake of
                //  simplicity for to user, but we should double check if we
                //  should give the user the chance to set slippage for each
                //  step in the cross-chain operation.
                const getSwapAmountMin = (tokenA, tokenB) =>
                  masterOracle
                    .quote(tokenA, tokenB, repayAmount)
                    .then(_repayAmount =>
                      amountMinFromSlippage(_repayAmount, slippage)
                    )

                const contract = new web3.eth.Contract(
                  depositTokenAbi,
                  collateralToken
                )
                const { decimals, symbol } = findTokenByAddress(
                  collateralToken,
                  chainId
                )
                debug(
                  'Initiating cross chain flash repay of %s %s',
                  fromUnit(withdrawAmount, decimals),
                  symbol
                )
                const owner = transactionOptions.from || from

                const method = function (
                  address,
                  bridgeTokenAmountMin,
                  swapAmountMin
                ) {
                  const smartFarmingManagerContract = new web3.eth.Contract(
                    smartFarmingManagerAbi,
                    address
                  )
                  return smartFarmingManagerContract.methods.crossChainFlashRepay(
                    syntheticToken,
                    depositToken,
                    Big(withdrawAmount).toFixed(),
                    bridgeToken,
                    bridgeTokenAmountMin, // bridgeTokenAmountMin is the minimum output amount for the collateral->bridgeToken swap at the srcChain (tx1)
                    swapAmountMin, // swapAmountOutMin_ is the minimum output amount for the bridgeToken->syntheticToken swap at the dstChain (tx2)
                    amountMinFromSlippage(repayAmount, slippage) // repayAmountMin_ is the minimum amount for repayment (tx3)
                  )
                }

                const { dryRun, ...txOptions } = transactionOptions

                if (dryRun) {
                  return pool.methods
                    .smartFarmingManager()
                    .call()
                    .then(smartFarmingManagerAddress =>
                      method(smartFarmingManagerAddress).call({
                        from: owner
                      })
                    )
                }

                const transactionsPromise = Promise.all([
                  isApprovalNeeded(
                    collateralToken,
                    owner,
                    poolAddress,
                    withdrawAmount
                  ),
                  pool.methods.smartFarmingManager().call(),
                  getSwapAmountMin(depositToken, bridgeToken),
                  getSwapAmountMin(bridgeToken, syntheticToken)
                ]).then(function ([
                  approvalNeeded,
                  smartFarmingManagerAddress,
                  bridgeTokenAmountMin,
                  swapAmountMin
                ]) {
                  const txs = []
                  if (approvalNeeded) {
                    txs.push({
                      gas: expectedGasFor.approval,
                      method: contract.methods.approve(
                        poolAddress,
                        Big(withdrawAmount).toFixed()
                      ),
                      suffix: 'approve'
                    })
                  }
                  txs.push({
                    gas: expectedGasFor.flashRepay,
                    method: method(
                      smartFarmingManagerAddress,
                      bridgeTokenAmountMin,
                      swapAmountMin
                    ),
                    suffix: 'crossChainFlashRepay'
                  })
                  return txs
                })

                const parseResults = function ([transactionData]) {
                  const { receipt } = transactionData
                  debug(
                    'Cross chain flash repay of %s %s completed',
                    fromUnit(withdrawAmount, decimals),
                    symbol
                  )
                  return { receipt }
                }

                return executeTransactions(
                  transactionsPromise,
                  parseResults,
                  txOptions
                )
              }

              return {
                approveAndDeposit,
                claimable,
                claimRewards,
                convert,
                convertFromUsd,
                convertToUsd,
                crossChainFlashRepay,
                crossChainLeverage,
                decodeInputFromTransaction,
                deposit,
                flashRepay,
                getCollateralBalances,
                getDebt,
                getDebtPosition,
                getDebtTokenBalances,
                getDeposit,
                getDepositByCollateral,
                getDepositTokenBalances,
                getSwapAndCallbackLzArgs,
                getSwapFee,
                getSwapFeeDiscountByEsMETBalance,
                getSwapFeeFor,
                getSyntheticTokenBalances,
                isApprovalNeeded,
                issue,
                leverage,
                parseCrossChainReceiptEvents,
                parseSmartFarmingReceiptEvents,
                quoteCallbackNativeFee,
                quoteCrossChainNativeFee,
                repay,
                repayAll,
                retryCrossChainLeverageCallback,
                retrySwapAndTriggerLeverageCallback,
                swap,
                uniqCollaterals,
                withdraw
              }
            })
          })
      )
    })
}

module.exports = createMetronomeSynthUser
